import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Pending,
  Cancel,
  Refresh,
  History,
  Info
} from '@mui/icons-material';
import EInvoiceService from '../../services/EInvoiceService';

const StatusTracker = ({ eInvoiceId, onStatusUpdate }) => {
  const [statusHistory, setStatusHistory] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);

  const statusSteps = [
    { key: 'Draft', label: 'Draft Created', description: 'E-invoice document created' },
    { key: 'Ready', label: 'Ready for Submission', description: 'Recipient information completed' },
    { key: 'Submitted', label: 'Submitted to LHDN', description: 'Document submitted to MyInvois' },
    { key: 'Valid', label: 'Validated', description: 'Document validated by LHDN' },
    { key: 'Cancelled', label: 'Cancelled', description: 'Document cancelled' },
    { key: 'Failed', label: 'Failed', description: 'Submission or validation failed' }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Valid':
        return <CheckCircle color="success" />;
      case 'Failed':
        return <Error color="error" />;
      case 'Cancelled':
        return <Cancel color="warning" />;
      case 'Submitted':
        return <Pending color="primary" />;
      default:
        return <Pending color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Valid':
        return 'success';
      case 'Failed':
        return 'error';
      case 'Cancelled':
        return 'warning';
      case 'Submitted':
        return 'primary';
      case 'Ready':
        return 'info';
      default:
        return 'default';
    }
  };

  const getActiveStep = (status) => {
    const stepIndex = statusSteps.findIndex(step => step.key === status);
    return stepIndex >= 0 ? stepIndex : 0;
  };

  const loadStatusHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const history = await EInvoiceService.getStatusHistory(eInvoiceId);
      setStatusHistory(history);
      
      if (history.length > 0) {
        setCurrentStatus(history[0].NewStatus);
      }
    } catch (err) {
      setError('Failed to load status history');
      console.error('Error loading status history:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Get latest status from LHDN
      const statusResponse = await EInvoiceService.getDocumentStatus(eInvoiceId);
      
      // Reload history to get any updates
      await loadStatusHistory();
      
      if (onStatusUpdate) {
        onStatusUpdate(statusResponse.status);
      }
    } catch (err) {
      setError('Failed to refresh status');
      console.error('Error refreshing status:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCancelEInvoice = async () => {
    if (!cancelReason.trim()) {
      setError('Please provide a reason for cancellation');
      return;
    }

    try {
      setCancelling(true);
      setError(null);
      
      await EInvoiceService.cancelEInvoice(eInvoiceId, { reason: cancelReason });
      
      setCancelDialogOpen(false);
      setCancelReason('');
      
      // Reload status history
      await loadStatusHistory();
      
      if (onStatusUpdate) {
        onStatusUpdate('Cancelled');
      }
    } catch (err) {
      setError('Failed to cancel e-invoice');
      console.error('Error cancelling e-invoice:', err);
    } finally {
      setCancelling(false);
    }
  };

  const canCancel = () => {
    return currentStatus === 'Submitted' || currentStatus === 'Valid';
  };

  const canRefresh = () => {
    return currentStatus === 'Submitted' || currentStatus === 'Valid';
  };

  useEffect(() => {
    if (eInvoiceId) {
      loadStatusHistory();
    }
  }, [eInvoiceId]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading status information...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            E-Invoice Status
          </Typography>
          <Box>
            {canRefresh() && (
              <Button
                startIcon={<Refresh />}
                onClick={refreshStatus}
                disabled={refreshing}
                size="small"
                sx={{ mr: 1 }}
              >
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            )}
            {canCancel() && (
              <Button
                startIcon={<Cancel />}
                onClick={() => setCancelDialogOpen(true)}
                color="warning"
                variant="outlined"
                size="small"
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Current Status */}
        <Box display="flex" alignItems="center" mb={3}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Current Status:
          </Typography>
          <Chip
            icon={getStatusIcon(currentStatus)}
            label={currentStatus}
            color={getStatusColor(currentStatus)}
            variant="filled"
          />
        </Box>

        {/* Status Progress */}
        <Stepper activeStep={getActiveStep(currentStatus)} orientation="vertical">
          {statusSteps.map((step, index) => (
            <Step key={step.key}>
              <StepLabel
                optional={
                  <Typography variant="caption">
                    {step.description}
                  </Typography>
                }
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Status History */}
        {statusHistory.length > 0 && (
          <Box mt={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <History sx={{ mr: 1 }} />
              Status History
            </Typography>
            <List dense>
              {statusHistory.map((history, index) => (
                <React.Fragment key={history.StatusHistoryId}>
                  <ListItem>
                    <ListItemIcon>
                      {getStatusIcon(history.NewStatus)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" fontWeight="medium">
                            {history.PreviousStatus ? 
                              `${history.PreviousStatus} → ${history.NewStatus}` : 
                              history.NewStatus
                            }
                          </Typography>
                          <Chip
                            label={history.NewStatus}
                            color={getStatusColor(history.NewStatus)}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(history.StatusDate).toLocaleString()}
                          </Typography>
                          {history.Notes && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {history.Notes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < statusHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {/* Cancel Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Cancel E-Invoice</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This action will cancel the e-invoice in LHDN system. This cannot be undone.
              </Typography>
            </Alert>
            <TextField
              autoFocus
              margin="dense"
              label="Reason for Cancellation"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Please provide a reason for cancelling this e-invoice..."
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCancelEInvoice}
              color="warning"
              variant="contained"
              disabled={cancelling || !cancelReason.trim()}
            >
              {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default StatusTracker;